'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Topbar from '@/components/layout/Topbar';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const services = [
{
  id: 'ai-marketing',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>,

  title: 'AI Digital Marketing',
  description: 'Harness the power of artificial intelligence to supercharge your digital marketing. Our AI-driven strategies use machine learning, predictive analytics, and intelligent automation to deliver smarter campaigns, better targeting, and significantly higher ROI than traditional marketing approaches.',
  features: ['AI-Powered Campaign Optimization', 'Predictive Audience Targeting', 'Automated Bid Management', 'AI Content Generation & Personalization', 'Intelligent A/B Testing', 'Real-Time AI Analytics & Insights'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19fd8fb0d-1771346866833.png",
  alt: 'AI digital marketing dashboard showing machine learning campaign optimization, predictive analytics and automated performance insights'
},
{
  id: 'seo',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>,

  title: 'SEO & Search Marketing',
  description: 'Dominate search engine rankings with our AI-enhanced SEO strategies. We combine proven SEO fundamentals with AI-powered keyword research, content optimization, and technical audits to drive organic traffic and qualified leads that convert.',
  features: ['Technical SEO Audit & Fixes', 'AI-Powered Keyword Research', 'On-Page & Off-Page Optimization', 'Link Building & Authority Growth', 'Local SEO for India & USA Markets', 'SEO Reporting & Analytics'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cda9365f-1772180350570.png",
  alt: 'SEO analytics dashboard showing keyword rankings, organic traffic growth, and search performance metrics'
},
{
  id: 'social',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
      </svg>,

  title: 'Social Media Marketing',
  description: 'Build a powerful social media presence that engages your audience and drives brand awareness. We manage your social channels with AI-assisted content creation and data-driven strategies that convert followers into loyal customers.',
  features: ['Social Media Strategy & Planning', 'AI-Assisted Content Creation', 'Community Management', 'Influencer Marketing Campaigns', 'Social Media Advertising (Meta, LinkedIn)', 'Analytics & Performance Reporting'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8f75c5b-1772109757124.png",
  alt: 'Social media marketing team planning content strategy with multiple social platform dashboards on screens'
},
{
  id: 'ppc',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>,

  title: 'PPC & Paid Advertising',
  description: 'Maximize your ROI with AI-optimized pay-per-click campaigns. Our PPC experts leverage machine learning to manage Google Ads, Meta Ads, and LinkedIn Ads — delivering measurable results and reducing cost per acquisition across India and US markets.',
  features: ['Google Ads Management', 'Meta (Facebook & Instagram) Ads', 'LinkedIn B2B Advertising', 'Display & Programmatic Advertising', 'AI-Powered Remarketing Campaigns', 'Conversion Rate Optimization'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19c13f543-1765872622903.png",
  alt: 'PPC advertising campaign performance metrics dashboard showing ROI, click-through rates, and conversion data'
},
{
  id: 'content',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>,

  title: 'Content Marketing',
  description: 'Create compelling AI-assisted content that attracts, engages, and converts your target audience. From SEO blog posts to video scripts and infographics, we develop a content strategy that positions you as an industry leader in your market.',
  features: ['AI-Assisted Content Strategy', 'SEO Blog Writing & Long-Form Content', 'Video Content Production', 'Infographic & Visual Content Design', 'Email Newsletter Content', 'Content Distribution & Amplification'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_130b085b5-1772171921489.png",
  alt: 'Content marketing strategist writing blog posts and creating digital content strategy on laptop'
},
{
  id: 'email',
  icon:
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>,

  title: 'Email Marketing & Automation',
  description: 'Build lasting customer relationships with AI-personalized email campaigns. We design, write, and automate intelligent email sequences that nurture leads, recover abandoned carts, and drive repeat business with precision targeting.',
  features: ['Email Campaign Strategy & Design', 'AI-Personalized Copywriting', 'Marketing Automation Workflows', 'Advanced List Segmentation', 'A/B Testing & Optimization', 'Performance Analytics & Reporting'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11ba05ea4-1773091040014.png",
  alt: 'Email marketing campaign dashboard showing open rates, click rates, and subscriber engagement metrics'
}];


const results = [
{ value: '400%', label: 'Average Traffic Increase' },
{ value: '6x', label: 'ROI on AI-Optimized Ad Spend' },
{ value: '300+', label: 'Campaigns Managed' },
{ value: '92%', label: 'Client Retention Rate' }];


export default function DigitalMarketingPage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animated');
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Topbar />
      <Header />
      {/* Page Hero */}
      <section className="page-hero-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-2 mb-5">
                <span className="text-primary text-sm font-semibold font-lato tracking-wide">PILLAR 1 OF 3 — AI-POWERED</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white font-poppins leading-tight mb-4">
                AI Digital Marketing
                <span className="block text-primary text-3xl md:text-4xl mt-2">& Digital Growth</span>
              </h1>
              <p className="text-white/70 font-lato text-lg leading-relaxed mb-8">
                We help businesses acquire customers and grow online through AI-powered digital marketing strategies — smarter campaigns, better targeting, and measurable results that outperform traditional marketing.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link href="/contact" className="bbt-btn-primary">Get Free AI Audit</Link>
                <Link href="#services" className="bbt-btn-outline border-white/40 text-white hover:bg-white hover:text-primary">Our Services</Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_154cf5064-1772433146848.png"
                alt="AI digital marketing analytics dashboard with charts showing website traffic, AI-driven conversions, and campaign performance"
                className="rounded-lg shadow-2xl" />
              
            </div>
          </div>
        </div>
      </section>

      {/* AI Marketing Highlight Banner */}
      <section className="py-10 bg-gradient-to-r from-blue-900 to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center text-3xl flex-shrink-0">🤖</div>
              <div>
                <h3 className="text-white font-bold font-poppins text-lg">AI-First Digital Marketing Agency</h3>
                <p className="text-white/70 font-lato text-sm">We integrate AI tools and machine learning into every campaign for superior performance and ROI</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {['AI Content Generation', 'Predictive Analytics', 'Smart Bidding', 'Automated Personalization']?.map((tag, i) =>
              <span key={i} className="bg-white/10 border border-white/20 text-white px-4 py-1.5 rounded-full text-xs font-lato font-semibold">{tag}</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Results Stats */}
      <section className="bbt-stats-section py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {results?.map((stat, i) =>
            <div key={i}>
                <div className="text-4xl font-bold text-white font-poppins mb-2">{stat?.value}</div>
                <div className="text-white/70 text-sm font-lato">{stat?.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="bbt-section-title">OUR DIGITAL MARKETING SERVICES</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3 mb-4"></div>
            <p className="bbt-section-subtitle">AI-powered digital marketing solutions to grow your business online — faster and smarter</p>
          </div>
          <div className="space-y-16">
            {services?.map((service, i) =>
            <div
              key={service?.id}
              id={service?.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center animate-on-scroll ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              
                <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                      {service?.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-text-main font-poppins">{service?.title}</h3>
                  </div>
                  {service?.id === 'ai-marketing' &&
                <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 mb-4">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                      <span className="text-blue-700 text-xs font-semibold font-lato tracking-wide">FEATURED — AI-POWERED SERVICE</span>
                    </div>
                }
                  <p className="text-gray-600 font-poppins text-base leading-relaxed mb-6">{service?.description}</p>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {service?.features?.map((f, fi) =>
                  <div key={fi} className="flex items-center gap-2 text-sm text-gray-600 font-lato">
                        <svg className="w-4 h-4 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {f}
                      </div>
                  )}
                  </div>
                  <Link href="/contact" className="bbt-btn-primary text-sm">Get Started</Link>
                </div>
                <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                  src={service?.image}
                  alt={service?.alt}
                  className="w-full rounded-lg shadow-card-hover" />
                
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why BBT Digital */}
      <section className="py-20 bg-secondary-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="bbt-section-title">WHY CHOOSE BBT FOR AI DIGITAL MARKETING?</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
            { icon: '🤖', title: 'AI-First Approach', desc: 'We integrate AI and machine learning into every campaign — from content generation to bid optimization — for results that outperform traditional marketing.' },
            { icon: '📊', title: 'Data-Driven Decisions', desc: 'Every decision is backed by real-time data and AI analytics. We track, measure, and optimize campaigns continuously for maximum ROI.' },
            { icon: '🎯', title: 'Precision Targeting', desc: 'AI-powered audience segmentation and predictive targeting ensures your ads reach the right people at the right time — reducing wasted spend.' },
            { icon: '🚀', title: 'Proven Results', desc: 'Our clients see an average 400% increase in organic traffic and 6x ROI on AI-optimized paid advertising campaigns.' },
            { icon: '🌍', title: 'India & USA Market Expertise', desc: 'Deep market knowledge across India and the USA, enabling us to run campaigns that resonate with local audiences in both markets.' },
            { icon: '💬', title: 'Transparent Reporting', desc: 'Monthly AI-powered reports with clear metrics, insights, and recommendations. You always know exactly what\'s working and why.' }]?.map((item, i) =>
            <div key={i} className="bbt-value-card animate-on-scroll" style={{ animationDelay: `${i % 3 * 0.1}s` }}>
                <div className="text-3xl mb-3">{item?.icon}</div>
                <h4 className="font-bold text-text-main font-lato text-sm uppercase tracking-wide mb-3">{item?.title}</h4>
                <p className="text-gray-600 text-sm font-poppins leading-relaxed">{item?.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* AI Tools We Use */}
      <section className="py-16" style={{ backgroundColor: '#6d97bf' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white font-poppins mb-3">AI TOOLS & TECHNOLOGIES WE USE</h2>
            <div className="w-16 h-1 bg-white/50 mx-auto"></div>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {['ChatGPT & GPT-4', 'Google AI / Gemini', 'Jasper AI', 'Midjourney', 'HubSpot AI', 'Semrush AI', 'Surfer SEO', 'Adzooma AI', 'Persado', 'Phrasee', 'Albert.ai', 'Brandwatch AI']?.map((tool, i) =>
            <span key={i} className="bg-white/10 border border-white/20 text-white px-5 py-2 rounded-full text-sm font-lato font-semibold hover:bg-white hover:text-primary transition-all cursor-default">
                {tool}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bbt-cta-section rounded-lg">
            <h2 className="text-2xl font-bold text-text-main font-poppins mb-3 uppercase tracking-wide">READY TO GROW WITH AI DIGITAL MARKETING?</h2>
            <p className="text-gray-600 font-poppins mb-8 text-lg">&ldquo;Get a free AI-powered digital marketing audit and discover your growth opportunities&rdquo;</p>
            <Link href="/contact" className="bbt-btn-primary">GET FREE AI AUDIT</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>);

}